import { useState } from 'react';
import type { AdminOrganizationDetail, Contract } from '@traceio-org/platform-contracts';
import { formatMoney, parseMoneyToMicros } from '../../../billing/money';
import {
  CheckboxGroup,
  RadioGroup,
  ReasonField,
  SelectField,
  TextField,
  reasonError,
} from '../../../components/fields';
import { WriteDialog } from '../../../components/WriteDialog';
import { useAdmin } from '../../../context';
import { formatCount, isDay, plural, productName, startOfDay, utcDay, versionLabel } from '../../../format';
import { useAdminRead } from '../../../useAdminRead';
import { BILLED_PRODUCTS, isBilledProductKey, type BilledProductKey } from '../../billing/shared';

const CAP_CHOICES = [
  { value: 'none', label: 'No spend cap', description: 'Usage is billed as it comes.' },
  {
    value: 'cap',
    label: 'A monthly spend cap',
    description: 'New work waits once the period reaches it; work already admitted finishes.',
  },
] as const;
type CapMode = (typeof CAP_CHOICES)[number]['value'];

const MAX_CONNECTIONS = 100_000;

interface SignForm {
  productKey: BilledProductKey;
  planVersionId: string;
  starts: string;
  withTrial: boolean;
  connections: string;
  capMode: CapMode;
  cap: string;
  reason: string;
}
type SignErrors = Partial<Record<keyof SignForm, string>>;

function firstBilledProduct(organization: AdminOrganizationDetail): BilledProductKey {
  const subscribed = organization.products.map((p) => p.productKey).find(isBilledProductKey);
  return subscribed ?? 'enterprise';
}

/**
 * Signing a contract: a published plan version of the product (the catalog ones and this organization's own custom
 * ones, read from platform-api when the dialog opens), the first day (UTC), the plan's trial when the version has
 * one, the purchased carrier connections, the monthly spend cap and a reason. The staff member who signs approves
 * it. Prices are shown from the version as platform-api sends it.
 */
export function SignContractDialog({
  organization,
  open,
  onClose,
  onSaved,
}: {
  organization: AdminOrganizationDetail;
  open: boolean;
  onClose: () => void;
  onSaved: (contract: Contract) => void;
}) {
  const { api } = useAdmin();
  const [form, setForm] = useState<SignForm>(() => ({
    productKey: firstBilledProduct(organization),
    planVersionId: '',
    starts: utcDay(Date.now()),
    withTrial: false,
    connections: '0',
    capMode: 'none',
    cap: '',
    reason: '',
  }));
  const [errors, setErrors] = useState<SignErrors>({});
  const set = <K extends keyof SignForm>(field: K, value: SignForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  // Published versions of the product: the catalog's and this organization's custom ones.
  const versionsKey = open ? `published-versions\u0000${form.productKey}` : null;
  const versionsRead = useAdminRead(versionsKey, (signal) =>
    api.listPlanVersions({ productKey: form.productKey, status: 'published' }, signal),
  );
  const versions =
    versionsRead.dataKey === versionsKey && !versionsRead.failure
      ? (versionsRead.data?.items ?? []).filter(
          (v) => v.organizationId === null || v.organizationId === organization.id,
        )
      : null;
  // The chosen version's content says whether it has a trial, and in which currency the cap is.
  const versionKey = open && form.planVersionId ? `plan-version\u0000${form.planVersionId}` : null;
  const versionRead = useAdminRead(versionKey, (signal) => api.getPlanVersion(form.planVersionId, signal));
  const version = versionRead.dataKey === versionKey && !versionRead.failure ? versionRead.data : null;
  const trial = version?.content.trial ?? null;
  const currency = version?.content.currency ?? 'the plan currency';

  const versionOptions = [
    { value: '', label: versions === null ? 'Loading the published versions' : 'Choose a plan version' },
    ...(versions ?? []).map((v) => ({
      value: v.id,
      label: `${v.planName} ${versionLabel(v.version)}${v.organizationId ? ' (custom, this organization)' : ' (catalog)'}`,
    })),
  ];

  const validate = () => {
    const found: SignErrors = {};
    if (!form.planVersionId) found.planVersionId = 'Choose a published plan version.';
    if (!isDay(form.starts)) found.starts = 'Enter the first day, as a date.';
    const connections = form.connections.trim();
    if (!/^\d+$/.test(connections)) found.connections = 'Enter a whole number of connections.';
    else if (Number(connections) > MAX_CONNECTIONS)
      found.connections = `At most ${formatCount(MAX_CONNECTIONS)} connections.`;
    if (form.capMode === 'cap') {
      const micros = parseMoneyToMicros(form.cap);
      if (micros === null) found.cap = 'Enter the cap as a number, for example 1500 or 1500.50.';
      else if (micros <= 0) found.cap = 'The cap must be above zero.';
    }
    const reason = reasonError(form.reason);
    if (reason) found.reason = reason;
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const base = version?.content.components.find((c) => c.kind === 'flat_recurring');

  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title={`Sign a contract for ${organization.displayName}`}
      description="You approve the contract by signing it. Only a published plan version can be signed."
      submitLabel="Sign the contract"
      submitHidden={organization.billingExempt === true}
      onSubmit={async () => {
        if (!validate()) return 'invalid';
        const contract = await api.createContract(organization.id, {
          productKey: form.productKey,
          planVersionId: form.planVersionId,
          startsAt: startOfDay(form.starts),
          withTrial: Boolean(trial) && form.withTrial,
          carrierConnections: Number(form.connections.trim()),
          spendCapMicros: form.capMode === 'cap' ? parseMoneyToMicros(form.cap) : null,
          reason: form.reason,
        });
        onSaved(contract);
        return undefined;
      }}
      onSaved={() => undefined}
    >
      {organization.billingExempt ? (
        <p role="alert">
          This organization is internal (not billed), so no contract can be signed for it. Change its billing on this
          page first.
        </p>
      ) : null}
      <SelectField
        label="Product"
        value={form.productKey}
        options={BILLED_PRODUCTS.map((key) => ({ value: key, label: productName(key) }))}
        onChange={(value) => {
          if (isBilledProductKey(value)) set('productKey', value);
          set('planVersionId', '');
          set('withTrial', false);
        }}
        required
      />
      <SelectField
        label="Plan version"
        value={form.planVersionId}
        options={versionOptions}
        onChange={(value) => {
          set('planVersionId', value);
          set('withTrial', false);
        }}
        hint={
          versionsRead.failure
            ? 'The published versions could not be loaded; try again in a moment.'
            : versions && versions.length === 0
              ? `No published version of ${productName(form.productKey)} this organization may sign. Publish one first.`
              : 'Catalog versions, and the custom versions of this organization.'
        }
        error={errors.planVersionId ?? null}
        required
      />
      {version ? (
        <p className="admin-muted">
          {base && base.kind === 'flat_recurring'
            ? `Base ${formatMoney(base.amountMicros, version.content.currency)} per period, in ${version.content.currency}. `
            : `Billed in ${version.content.currency}. `}
          {trial
            ? `The version has a ${plural(trial.days, 'day', 'days')} trial${trial.cardRequired ? ', a card required up front' : ''}.`
            : 'The version has no trial.'}
        </p>
      ) : null}
      <TextField
        label="First day"
        type="date"
        value={form.starts}
        onChange={(value) => set('starts', value)}
        hint="From the start of the day, UTC. The monthly periods are anchored there, or at the end of the trial."
        error={errors.starts ?? null}
        required
      />
      {trial ? (
        <CheckboxGroup
          legend="Trial"
          choices={[
            {
              value: 'trial',
              label: `Start with the plan's trial: ${plural(trial.days, 'day', 'days')}, nothing charged`,
              description: trial.cardRequired ? 'A card is required up front.' : 'No card is required.',
            },
          ]}
          values={form.withTrial ? ['trial'] : []}
          onChange={(values) => set('withTrial', values.includes('trial'))}
        />
      ) : null}
      <TextField
        label="Carrier connections"
        value={form.connections}
        onChange={(value) => set('connections', value)}
        type="number"
        min={0}
        max={MAX_CONNECTIONS}
        hint="The purchased number of carrier connections."
        error={errors.connections ?? null}
        inputMode="numeric"
        required
      />
      <RadioGroup
        legend="Spend cap"
        choices={CAP_CHOICES}
        value={form.capMode}
        onChange={(value) => set('capMode', value as CapMode)}
      />
      {form.capMode === 'cap' ? (
        <TextField
          label={`Monthly spend cap (${currency})`}
          value={form.cap}
          onChange={(value) => set('cap', value)}
          hint="Usage only. It must leave room for the token price's minimum charge for a period."
          error={errors.cap ?? null}
          inputMode="decimal"
          required
        />
      ) : null}
      <ReasonField
        value={form.reason}
        onChange={(value) => set('reason', value)}
        error={errors.reason ?? null}
      />
    </WriteDialog>
  );
}
